import { CreditAccount } from "../components/CreditAccount";
import { SubscriptionActionDialog } from "../components/SubscriptionActionDialog";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type {
  FirstPeriodPolicy,
  Invoice,
  Member,
  Package,
  PeriodSkip,
  SubscriptionView,
  UpdateMemberInput,
} from "@vereinfach/types";
import { api, ApiError, capExceeded, errorMessage, mutate } from "../lib/api";
import {
  coverageLabelText,
  formatCHF,
  formatDate,
  formatIsoDate,
  formatPeriodEnd,
  formatPeriodRange,
  monthsLabel,
  todayIso,
} from "../lib/format";
import { Modal } from "../components/Modal";
import { StatusBadge } from "../components/StatusBadge";

type ChangePreview = {
  retract_draft_invoice_ids: string[];
  issued_future_invoice_ids: string[];
  current_draft_invoice_ids: string[];
  open_periods: { start: number; end: number }[];
};

type Notice = { kind: "info" | "error"; text: string };

type ConfirmState = {
  kind: "auto_renew_off" | "pause" | "end_now";
  sub: SubscriptionView;
  preview: ChangePreview | null;
};

/** Add calendar months in UTC (period grids live on UTC midnights). */
function addMonthsUtc(unixSeconds: number, months: number): number {
  const d = new Date(unixSeconds * 1000);
  d.setUTCMonth(d.getUTCMonth() + months);
  return Math.floor(d.getTime() / 1000);
}

function isoFromUnix(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

function monthName(month: number): string {
  return new Date(Date.UTC(2000, month - 1, 1)).toLocaleDateString("de-CH", {
    month: "long",
    timeZone: "UTC",
  });
}

export function MemberDetailPage({ tenantId, memberId }: { tenantId: string; memberId: string }) {
  const navigate = useNavigate();
  const base = `/tenants/${tenantId}`;

  const [member, setMember] = useState<Member | null>(null);
  const [subs, setSubs] = useState<SubscriptionView[]>([]);
  const [skips, setSkips] = useState<PeriodSkip[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmState | null>(null);
  const [actionSub, setActionSub] = useState<SubscriptionView | null>(null);
  const [periodSub, setPeriodSub] = useState<SubscriptionView | null>(null);
  const [skipSub, setSkipSub] = useState<SubscriptionView | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  async function refresh() {
    try {
      const [m, s, inv, p] = await Promise.all([
        api<Member>(`${base}/members/${memberId}`),
        api<{ subscriptions: SubscriptionView[]; skips: PeriodSkip[] }>(
          `${base}/members/${memberId}/subscriptions`,
        ),
        api<{ invoices: Invoice[] }>(`${base}/members/${memberId}/invoices`),
        api<{ packages: Package[] }>(`${base}/packages`),
      ]);
      setMember(m);
      setSubs(s.subscriptions);
      setSkips(s.skips);
      setInvoices(inv.invoices);
      setPackages(p.packages);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, memberId]);

  const subById = useMemo(() => new Map(subs.map((s) => [s.id, s])), [subs]);
  const runningSubs = subs.filter((s) => s.status !== "ended");
  const endedSubs = subs.filter((s) => s.status === "ended");
  const canDelete = subs.length === 0 && invoices.length === 0;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setNotice({ kind: "error", text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function onToggleAutoRenew(sub: SubscriptionView) {
    if (busy) return;
    if (!sub.auto_renew) {
      await run(async () => {
        await mutate(`${base}/subscriptions/${sub.id}/auto-renew`, { auto_renew: true });
      });
      return;
    }
    setBusy(true);
    try {
      const preview = await api<ChangePreview>(`${base}/subscriptions/${sub.id}/change-preview`);
      if (
        preview.retract_draft_invoice_ids.length > 0 ||
        preview.issued_future_invoice_ids.length > 0
      ) {
        setConfirmAction({ kind: "auto_renew_off", sub, preview });
      } else {
        await mutate(`${base}/subscriptions/${sub.id}/auto-renew`, { auto_renew: false });
        await refresh();
      }
    } catch (e) {
      setNotice({ kind: "error", text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  function onPauseClick(sub: SubscriptionView) {
    setActionSub(sub);
  }
  function onResume(sub: SubscriptionView) {
    setActionSub(sub);
  }

  async function onEndNowClick(sub: SubscriptionView) {
    setBusy(true);
    try {
      const preview = await api<ChangePreview>(`${base}/subscriptions/${sub.id}/change-preview`);
      setConfirmAction({ kind: "end_now", sub, preview });
    } catch (e) {
      setNotice({ kind: "error", text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function executeConfirm() {
    if (!confirmAction) return;
    const { kind, sub } = confirmAction;
    setConfirmAction(null);
    await run(async () => {
      if (kind === "auto_renew_off") {
        await mutate(`${base}/subscriptions/${sub.id}/auto-renew`, { auto_renew: false });
      } else if (kind === "pause") {
        await mutate(`${base}/subscriptions/${sub.id}/pause`);
      } else {
        await mutate(`${base}/subscriptions/${sub.id}/end-now`, { open_periods: "skip" });
      }
    });
  }

  async function onDraftNow(sub: SubscriptionView) {
    setBusy(true);
    try {
      const { invoice_id } = await mutate<{ invoice_id: string }>(
        `${base}/subscriptions/${sub.id}/draft-now`,
      );
      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice_id } });
    } catch (e) {
      setNotice({ kind: "error", text: errorMessage(e) });
      setBusy(false);
    }
  }

  async function onUnskip(skip: PeriodSkip) {
    if (!window.confirm("Die Periode wird wieder verrechnet (neuer Entwurf).")) return;
    await run(async () => {
      await mutate(`${base}/subscriptions/${skip.subscription_id}/unskip`, {
        period_start: skip.period_start,
      });
    });
  }

  async function onUnleave() {
    if (!window.confirm("Austritt rückgängig machen?")) return;
    await run(async () => {
      await mutate(`${base}/members/${memberId}/unleave`);
    });
  }

  async function onArchive() {
    if (
      !window.confirm(
        "Mitglied archivieren? Archivierte Mitglieder zählen nicht zum Mitglieder-Limit und erscheinen nicht mehr in Listen und Finanzen.",
      )
    )
      return;
    await run(async () => {
      await mutate(`${base}/members/${memberId}/archive`);
    });
  }

  async function onUnarchive() {
    setBusy(true);
    try {
      await mutate(`${base}/members/${memberId}/unarchive`);
      await refresh();
    } catch (e) {
      const cap = capExceeded(e);
      setNotice({
        kind: "error",
        text: cap
          ? "Wiederherstellen würde das Mitglieder-Limit überschreiten — zuerst upgraden (Einstellungen → Abo & Rechnungen)."
          : errorMessage(e),
      });
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!window.confirm("Dieses Mitglied endgültig löschen?")) return;
    setBusy(true);
    try {
      await mutate(`${base}/members/${memberId}`, {}, "DELETE");
      navigate({ to: "/members" });
    } catch (e) {
      setNotice({ kind: "error", text: errorMessage(e) });
      setBusy(false);
    }
  }

  if (loading) return <p className="text-slate-400">Lädt…</p>;
  if (!member) {
    return (
      <div>
        <p className="text-sm text-red-600">{error ?? "Mitglied nicht gefunden."}</p>
        <Link to="/members" className="mt-2 inline-block text-sm text-slate-500 hover:underline">
          ‹ Zurück zu den Mitgliedern
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link to="/members" className="text-xs text-slate-500 hover:text-slate-900">
        ‹ Mitglieder
      </Link>

      <div className="mt-1 mb-5 flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold tracking-tight">
          {member.first_name} {member.last_name}
        </h2>
        <MemberStatusChip status={member.status} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button onClick={() => setShowEdit(true)} className="btn-sm">
            Bearbeiten
          </button>
          {member.status === "active" && (
            <button
              onClick={() => setShowLeave(true)}
              className="btn-sm border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              Austritt
            </button>
          )}
          {member.status === "archived" ? (
            <button onClick={onUnarchive} disabled={busy} className="btn-sm disabled:opacity-50">
              Wiederherstellen
            </button>
          ) : (
            <button onClick={onArchive} disabled={busy} className="btn-sm disabled:opacity-50">
              Archivieren
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              disabled={busy}
              className="btn-sm border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Löschen
            </button>
          )}
        </div>
      </div>

      {member.status === "left" && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-600">
          <span>Ausgetreten{member.left_at ? ` am ${formatDate(member.left_at)}` : ""}.</span>
          <button
            onClick={onUnleave}
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Austritt rückgängig
          </button>
        </div>
      )}

      {member.status === "archived" && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-600">
          <span>
            Archiviert
            {member.deleted_at ? ` am ${formatDate(member.deleted_at)}` : ""} — zählt nicht zum
            Mitglieder-Limit.
          </span>
          <button
            onClick={onUnarchive}
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Wiederherstellen
          </button>
        </div>
      )}

      {notice && (
        <div
          className={
            "mb-5 flex items-start justify-between gap-3 rounded-xl px-4 py-3 text-sm " +
            (notice.kind === "error" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700")
          }
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-60 hover:opacity-100">
            ✕
          </button>
        </div>
      )}

      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
          Kontakt
        </h3>
        <dl className="space-y-1 text-sm">
          {member.org_name && <Row label="Organisation" value={member.org_name} />}
          <Row label="E-Mail" value={member.email} />
          <Row label="Telefon" value={member.phone} />
          <Row
            label="Adresse"
            value={
              member.address_line1
                ? [
                    member.address_line1,
                    member.address_line2,
                    `${member.zip ?? ""} ${member.city ?? ""}`.trim(),
                  ]
                    .filter(Boolean)
                    .join(", ")
                : null
            }
          />
          <Row
            label="Geburtsdatum"
            value={member.date_of_birth ? formatIsoDate(member.date_of_birth) : null}
          />
          <Row label="Eintritt" value={formatDate(member.joined_at)} />
        </dl>
        {member.notes && (
          <p className="mt-3 border-t border-slate-100 pt-3 text-sm whitespace-pre-wrap text-slate-600">
            {member.notes}
          </p>
        )}
      </section>

      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Beiträge</h3>
          {member.status === "active" && (
            <button
              onClick={() => setShowAssign(true)}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              Beitrag zuteilen
            </button>
          )}
        </div>

        {runningSubs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
            Kein laufender Beitrag.
          </div>
        ) : (
          <div className="space-y-3">
            {runningSubs.map((sub) => (
              <div key={sub.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {sub.package_name} · {formatCHF(sub.package_price_rappen)} /{" "}
                      {monthsLabel(sub.package_duration_months)}
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      {coverageLabelText(sub.coverage_label)}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      Aktuelle Periode: {formatPeriodRange(sub.current_start, sub.current_end)}
                    </div>
                  </div>
                  <SubStatusChip status={sub.status} />
                </div>

                {sub.stale_issued_invoice_ids.length > 0 && (
                  <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Rechnung für künftige Periode besteht —{" "}
                    <Link
                      to="/invoices/$invoiceId"
                      params={{ invoiceId: sub.stale_issued_invoice_ids[0]! }}
                      className="font-medium underline hover:no-underline"
                    >
                      prüfen
                    </Link>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Switch
                      checked={!!sub.auto_renew}
                      disabled={busy}
                      onChange={() => onToggleAutoRenew(sub)}
                    />
                    verlängert sich automatisch
                  </label>
                  <div className="ml-auto flex items-center gap-2">
                    {sub.status === "active" ? (
                      <button
                        className="btn-sm disabled:opacity-50"
                        disabled={busy}
                        onClick={() => onPauseClick(sub)}
                      >
                        Pausieren
                      </button>
                    ) : (
                      <button
                        className="btn-sm disabled:opacity-50"
                        disabled={busy}
                        onClick={() => onResume(sub)}
                      >
                        Fortsetzen
                      </button>
                    )}
                    <div className="relative">
                      <button
                        className="btn-sm"
                        aria-label="Weitere Aktionen"
                        onClick={() => setMenuFor(menuFor === sub.id ? null : sub.id)}
                      >
                        ⋯
                      </button>
                      {menuFor === sub.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuFor(null)} />
                          <div className="absolute right-0 z-20 mt-2 min-w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                            <MenuItem
                              onClick={() => {
                                setMenuFor(null);
                                onDraftNow(sub);
                              }}
                            >
                              Rechnung jetzt erstellen
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setMenuFor(null);
                                setPeriodSub(sub);
                              }}
                            >
                              Periode anpassen
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                setMenuFor(null);
                                setSkipSub(sub);
                              }}
                            >
                              Periode überspringen
                            </MenuItem>
                            <MenuItem
                              danger
                              onClick={() => {
                                setMenuFor(null);
                                onEndNowClick(sub);
                              }}
                            >
                              Sofort beenden
                            </MenuItem>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {endedSubs.length > 0 && (
          <div className="mt-3 space-y-2">
            {endedSubs.map((sub) => (
              <div
                key={sub.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500"
              >
                <span>
                  {sub.package_name} · {formatPeriodRange(sub.current_start, sub.current_end)}
                </span>
                <span>Beendet{sub.ended_at ? ` am ${formatDate(sub.ended_at)}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {skips.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            Übersprungene Perioden
          </h3>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white text-sm">
            {skips.map((sk) => (
              <div
                key={`${sk.subscription_id}-${sk.period_start}`}
                className="flex flex-wrap items-center gap-3 px-4 py-2.5"
              >
                <span className="whitespace-nowrap">
                  {formatPeriodRange(sk.period_start, sk.period_end)}
                </span>
                {subById.get(sk.subscription_id) && (
                  <span className="text-xs text-slate-400">
                    {subById.get(sk.subscription_id)!.package_name}
                  </span>
                )}
                <span className="text-xs text-slate-500">{sk.note}</span>
                <button
                  className="btn-sm ml-auto disabled:opacity-50"
                  disabled={busy}
                  onClick={() => onUnskip(sk)}
                >
                  Aufheben
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">
          Rechnungen
        </h3>
        {invoices.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Rechnungen.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Nummer</th>
                  <th className="px-4 py-2.5 text-left font-medium">Datum</th>
                  <th className="px-4 py-2.5 text-right font-medium">Betrag</th>
                  <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium">Offen</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    onClick={() =>
                      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: inv.id } })
                    }
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {inv.number ? (
                        <span className="font-mono text-xs">{inv.number}</span>
                      ) : (
                        <span className="text-slate-500 italic">Entwurf</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">
                      {formatIsoDate(inv.invoice_date)}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {formatCHF(inv.total_rappen)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={inv.display_status} />
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap text-slate-500">
                      {inv.balance_rappen > 0 ? formatCHF(inv.balance_rappen) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showEdit && (
        <EditMemberModal
          base={base}
          member={member}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            refresh();
          }}
        />
      )}

      {showLeave && (
        <LeaveModal
          base={base}
          memberId={memberId}
          subs={subs}
          onClose={() => setShowLeave(false)}
          onLeft={() => {
            setShowLeave(false);
            refresh();
          }}
        />
      )}

      {showAssign && (
        <AssignSubscriptionModal
          base={base}
          memberId={memberId}
          packages={packages}
          onClose={() => setShowAssign(false)}
          onAssigned={(pastPeriodCount) => {
            setShowAssign(false);
            if (pastPeriodCount > 0) {
              setNotice({
                kind: "info",
                text: `${pastPeriodCount} ${
                  pastPeriodCount === 1 ? "Entwurf" : "Entwürfe"
                } für vergangene Perioden erstellt — unter Rechnungen prüfen.`,
              });
            }
            refresh();
          }}
        />
      )}

      <CreditAccount tenantId={tenantId} memberId={memberId} />
      {actionSub && (
        <SubscriptionActionDialog
          tenantId={tenantId}
          subscription={actionSub}
          onClose={() => setActionSub(null)}
          onDone={refresh}
        />
      )}
      {confirmAction && (
        <Modal
          title={
            confirmAction.kind === "auto_renew_off"
              ? "Automatische Verlängerung stoppen?"
              : confirmAction.kind === "pause"
                ? "Beitrag pausieren?"
                : "Beitrag sofort beenden?"
          }
          onClose={() => setConfirmAction(null)}
        >
          {confirmAction.kind === "pause" && (
            <p className="text-sm text-slate-600">Keine neuen Rechnungen, bis du fortsetzt.</p>
          )}
          {confirmAction.kind === "end_now" && (
            <p className="text-sm text-slate-600">Alle offenen Perioden werden übersprungen.</p>
          )}
          {confirmAction.kind === "end_now" &&
            confirmAction.preview &&
            confirmAction.preview.open_periods.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-xs text-slate-500">
                {confirmAction.preview.open_periods.map((p) => (
                  <li key={p.start}>{formatPeriodRange(p.start, p.end)}</li>
                ))}
              </ul>
            )}
          {confirmAction.preview && confirmAction.preview.retract_draft_invoice_ids.length > 0 && (
            <p className="mt-2 text-sm text-slate-600">
              Entwurf für die nächste Periode wird gelöscht.
            </p>
          )}
          {confirmAction.preview && confirmAction.preview.issued_future_invoice_ids.length > 0 && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Rechnung für künftige Periode ist bereits ausgestellt — ggf. stornieren.
            </p>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmAction(null)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={executeConfirm}
              className={
                "rounded-lg px-3 py-1.5 text-sm text-white " +
                (confirmAction.kind === "end_now"
                  ? "bg-red-600 hover:bg-red-500"
                  : "bg-slate-900 hover:bg-slate-800")
              }
            >
              {confirmAction.kind === "auto_renew_off"
                ? "Stoppen"
                : confirmAction.kind === "pause"
                  ? "Pausieren"
                  : "Beenden"}
            </button>
          </div>
        </Modal>
      )}

      {periodSub && (
        <SetPeriodModal
          base={base}
          sub={periodSub}
          onClose={() => setPeriodSub(null)}
          onSaved={() => {
            setPeriodSub(null);
            refresh();
          }}
        />
      )}

      {skipSub && (
        <SkipPeriodModal
          base={base}
          sub={skipSub}
          onClose={() => setSkipSub(null)}
          onSaved={() => {
            setSkipSub(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// --- small presentational helpers -------------------------------------------

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value ?? "—"}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function MemberStatusChip({ status }: { status: Member["status"] }) {
  if (status === "active") {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700">
        Aktiv
      </span>
    );
  }
  if (status === "archived") {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-400">
        Archiviert
      </span>
    );
  }
  return (
    <span className="inline-block rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-500">
      Ausgetreten
    </span>
  );
}

function SubStatusChip({ status }: { status: SubscriptionView["status"] }) {
  const style =
    status === "active"
      ? "bg-emerald-50 text-emerald-700"
      : status === "paused"
        ? "bg-amber-50 text-amber-700"
        : "bg-slate-100 text-slate-500";
  const label = status === "active" ? "Aktiv" : status === "paused" ? "Pausiert" : "Beendet";
  return <span className={`inline-block rounded px-2 py-0.5 text-xs ${style}`}>{label}</span>;
}

function Switch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 " +
        (checked ? "bg-emerald-500" : "bg-slate-300")
      }
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
          (checked ? "translate-x-4" : "translate-x-1")
        }
      />
    </button>
  );
}

function MenuItem({
  danger,
  onClick,
  children,
}: {
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "block w-full rounded-lg px-3 py-2 text-left text-sm " +
        (danger ? "text-red-600 hover:bg-red-50" : "text-slate-700 hover:bg-slate-50")
      }
    >
      {children}
    </button>
  );
}

// --- modals ------------------------------------------------------------------

function EditMemberModal({
  base,
  member,
  onClose,
  onSaved,
}: {
  base: string;
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<UpdateMemberInput>({
    first_name: member.first_name,
    last_name: member.last_name,
    org_name: member.org_name,
    email: member.email,
    phone: member.phone,
    address_line1: member.address_line1,
    address_line2: member.address_line2,
    zip: member.zip,
    city: member.city,
    country: member.country,
    date_of_birth: member.date_of_birth,
    notes: member.notes,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof UpdateMemberInput>(k: K, v: UpdateMemberInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await mutate(`${base}/members/${member.id}`, { ...form }, "PATCH");
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Mitglied bearbeiten" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vorname">
            <input
              required
              autoFocus
              value={form.first_name ?? ""}
              onChange={(e) => set("first_name", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Nachname">
            <input
              required
              value={form.last_name ?? ""}
              onChange={(e) => set("last_name", e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <Field label="Organisation / juristischer Name (optional)">
          <input
            value={form.org_name ?? ""}
            onChange={(e) => set("org_name", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="E-Mail">
          <input
            type="email"
            value={form.email ?? ""}
            onChange={(e) => set("email", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Telefon">
          <input
            value={form.phone ?? ""}
            onChange={(e) => set("phone", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Strasse">
          <input
            value={form.address_line1 ?? ""}
            onChange={(e) => set("address_line1", e.target.value || null)}
            className="input"
          />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="PLZ">
            <input
              value={form.zip ?? ""}
              onChange={(e) => set("zip", e.target.value || null)}
              className="input"
            />
          </Field>
          <div className="col-span-2">
            <Field label="Ort">
              <input
                value={form.city ?? ""}
                onChange={(e) => set("city", e.target.value || null)}
                className="input"
              />
            </Field>
          </div>
        </div>
        <Field label="Geburtsdatum">
          <input
            type="date"
            value={form.date_of_birth ?? ""}
            onChange={(e) => set("date_of_birth", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Notizen">
          <textarea
            rows={3}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value || null)}
            className="input"
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LeaveModal({
  base,
  memberId,
  subs,
  onClose,
  onLeft,
}: {
  base: string;
  memberId: string;
  subs: SubscriptionView[];
  onClose: () => void;
  onLeft: () => void;
}) {
  const [at, setAt] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<ChangePreview[] | null>(null);

  useEffect(() => {
    const activeSubs = subs.filter((s) => s.status === "active");
    let cancelled = false;
    Promise.all(
      activeSubs.map((s) => api<ChangePreview>(`${base}/subscriptions/${s.id}/change-preview`)),
    )
      .then((res) => {
        if (!cancelled) setPreviews(res);
      })
      .catch(() => {
        if (!cancelled) setPreviews([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retractCount = previews?.reduce((n, p) => n + p.retract_draft_invoice_ids.length, 0) ?? 0;
  const issuedCount = previews?.reduce((n, p) => n + p.issued_future_invoice_ids.length, 0) ?? 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await mutate(`${base}/members/${memberId}/leave`, at ? { at } : {});
      onLeft();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Austritt" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-600">
          Beiträge verlängern sich nicht mehr; offene Entwürfe für künftige Perioden werden
          gelöscht.
        </p>
        {previews === null ? (
          <p className="text-xs text-slate-400">Prüfe offene Entwürfe…</p>
        ) : (
          <>
            {retractCount > 0 && (
              <p className="text-sm text-slate-600">
                {retractCount} {retractCount === 1 ? "Entwurf wird" : "Entwürfe werden"} gelöscht.
              </p>
            )}
            {issuedCount > 0 && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {issuedCount === 1
                  ? "Eine Rechnung für eine künftige Periode ist bereits ausgestellt"
                  : `${issuedCount} Rechnungen für künftige Perioden sind bereits ausgestellt`}{" "}
                — ggf. stornieren.
              </p>
            )}
          </>
        )}
        <Field label="Austritt per">
          <input type="date" value={at} onChange={(e) => setAt(e.target.value)} className="input" />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-500 disabled:opacity-50"
          >
            {busy ? "Speichern…" : "Austritt bestätigen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AssignSubscriptionModal({
  base,
  memberId,
  packages,
  onClose,
  onAssigned,
}: {
  base: string;
  memberId: string;
  packages: Package[];
  onClose: () => void;
  onAssigned: (pastPeriodCount: number) => void;
}) {
  const activePackages = packages.filter((p) => !p.is_archived);
  const [packageId, setPackageId] = useState(activePackages[0]?.id ?? "");
  const [startDate, setStartDate] = useState(todayIso());
  const [autoRenew, setAutoRenew] = useState(true);
  const [paidUntil, setPaidUntil] = useState("");
  const [policy, setPolicy] = useState<FirstPeriodPolicy>("full");
  const [askDuplicate, setAskDuplicate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pkg = activePackages.find((p) => p.id === packageId) ?? null;
  const pastWarning = startDate !== "" && startDate < todayIso() && !paidUntil;

  async function submit(allowDuplicate: boolean) {
    if (!pkg || !startDate) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        package_id: pkg.id,
        start_date: startDate,
        auto_renew: autoRenew,
      };
      if (paidUntil) body.paid_until = paidUntil;
      if (pkg.year_start_month != null) body.first_period_policy = policy;
      if (allowDuplicate) body.allow_duplicate = true;
      const res = await mutate<{ past_period_count: number }>(
        `${base}/members/${memberId}/subscription`,
        body,
      );
      onAssigned(res.past_period_count);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.data?.error === "duplicate_subscription") {
        setAskDuplicate(true);
      } else {
        setError(errorMessage(e));
      }
      setBusy(false);
    }
  }

  return (
    <Modal title="Beitrag zuteilen" onClose={onClose}>
      {activePackages.length === 0 ? (
        <>
          <p className="text-sm text-slate-500">
            Noch keine Beiträge angelegt. Lege zuerst unter «Beiträge» einen an.
          </p>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Schliessen
            </button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(askDuplicate);
          }}
          className="space-y-3"
        >
          <Field label="Beitrag">
            <select
              value={packageId}
              onChange={(e) => {
                setPackageId(e.target.value);
                setAskDuplicate(false);
              }}
              className="input"
            >
              {activePackages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          {pkg && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-900">{formatCHF(pkg.price_rappen)}</span>
              <span>/ {monthsLabel(pkg.duration_months)}</span>
              {pkg.year_start_month != null && (
                <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">
                  Vereinsjahr ab {monthName(pkg.year_start_month)}
                </span>
              )}
            </div>
          )}
          <Field label="Startdatum">
            <input
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
            />
            verlängert sich automatisch
          </label>
          <Field label="Bereits bezahlt bis (optional)">
            <input
              type="date"
              value={paidUntil}
              onChange={(e) => setPaidUntil(e.target.value)}
              className="input"
            />
          </Field>
          <p className="text-xs text-slate-500">
            Perioden vor diesem Datum werden übersprungen («vor Vereinfach bezahlt») — die erste
            Rechnung ist die nächste fällige.
          </p>
          {pastWarning && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Achtung: erstellt Rechnungsentwürfe für vergangene Perioden — falls schon bezahlt,
              ‹bereits bezahlt bis› setzen.
            </p>
          )}

          {pkg && pkg.year_start_month != null && (
            <fieldset>
              <legend className="text-xs font-medium text-slate-500">Erste Periode</legend>
              <div className="mt-1 space-y-1.5 text-sm">
                {(
                  [
                    { value: "full", label: "Volle Periode verrechnen" },
                    { value: "prorated", label: "Anteilig (pro rata)" },
                    { value: "free_until_grid", label: "Gratis bis Jahresbeginn" },
                  ] as { value: FirstPeriodPolicy; label: string }[]
                ).map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="first_period_policy"
                      checked={policy === opt.value}
                      onChange={() => setPolicy(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {askDuplicate && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Dieses Mitglied hat diesen Beitrag bereits. Trotzdem zuteilen?
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || !packageId || !startDate}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? "Zuteilen…" : askDuplicate ? "Trotzdem zuteilen" : "Zuteilen"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function SetPeriodModal({
  base,
  sub,
  onClose,
  onSaved,
}: {
  base: string;
  sub: SubscriptionView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lastDay, setLastDay] = useState(isoFromUnix(sub.current_end - 86400));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await mutate(`${base}/subscriptions/${sub.id}/set-period`, { last_day: lastDay });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Periode anpassen" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-600">
          Letzter Tag der aktuellen Periode (inklusive). Aktuell: {formatPeriodEnd(sub.current_end)}
          .
        </p>
        <Field label="Letzter Tag">
          <input
            type="date"
            required
            value={lastDay}
            onChange={(e) => setLastDay(e.target.value)}
            className="input"
            autoFocus
          />
        </Field>
        <p className="text-xs text-slate-500">Verlängerung wird als Kulanz vermerkt.</p>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || !lastDay}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SkipPeriodModal({
  base,
  sub,
  onClose,
  onSaved,
}: {
  base: string;
  sub: SubscriptionView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const periodStart = sub.current_end;
  const periodEnd = addMonthsUtc(sub.current_end, sub.package_duration_months);
  const [note, setNote] = useState("Übersprungen");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await mutate(`${base}/subscriptions/${sub.id}/skip`, {
        period_start: periodStart,
        period_end: periodEnd,
        note,
      });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Periode überspringen" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-600">
          Die nächste Periode wird übersprungen — dafür wird keine Rechnung erstellt.
        </p>
        <div className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <div className="text-xs text-slate-500">Nächste Periode</div>
          <div className="font-medium">{formatPeriodRange(periodStart, periodEnd)}</div>
        </div>
        <Field label="Notiz">
          <input
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="input"
            maxLength={200}
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || !note.trim()}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "Speichern…" : "Überspringen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
